import { BillingPlanId } from '@/modules/billing/constants/BillingPlanId';
import { BillingPlanType } from '@/modules/billing/constants/BillingPlanType';
import { BaseEntityModel } from '/@/modules/core/app/models/abstracts/BaseEntityModel';

export class BillingPlanMapping extends BaseEntityModel {
  id: number;

  planId: BillingPlanId;

  planName: string | null;

  type?: BillingPlanType;

  isRecommended?: boolean;

  constructor(model: Partial<BillingPlanMapping> = {}) {
    super(model);

    this.id = model.id as number;
    this.planId = model.planId as BillingPlanId;
    this.planName = model.planName ?? null;
    this.type = model.type;
    this.isRecommended = model.isRecommended;
  }

  get isEssentialPlan(): boolean {
    return [
      BillingPlanId.INDIVIDUAL_ESSENTIAL_PLAN,
      BillingPlanId.COUPLES_ESSENTIAL_PLAN,
      BillingPlanId.LEGACY_INDIVIDUAL_ESSENTIAL_PLAN,
      BillingPlanId.LEGACY_COUPLES_ESSENTIAL_PLAN
    ].includes(this.planId);
  }

  get isStandardPlan(): boolean {
    return [BillingPlanId.INDIVIDUAL_STANDARD_PLAN, BillingPlanId.COUPLES_STANDARD_PLAN].includes(
      this.planId
    );
  }

  get isCouplesStandardPlan(): boolean {
    return this.planId === BillingPlanId.COUPLES_STANDARD_PLAN;
  }

  get isBasicPlan(): boolean {
    return [BillingPlanId.INDIVIDUAL_BASIC_PLAN, BillingPlanId.COUPLES_BASIC_PLAN].includes(
      this.planId
    );
  }

  get isCouplesBasicPlan(): boolean {
    return this.planId === BillingPlanId.COUPLES_BASIC_PLAN;
  }

  get isIndividualStandardPlan(): boolean {
    return BillingPlanId.INDIVIDUAL_STANDARD_PLAN === this.planId;
  }

  get isPlusPlan(): boolean {
    return [BillingPlanId.INDIVIDUAL_PLUS_PLAN, BillingPlanId.COUPLES_PLUS_PLAN].includes(
      this.planId
    );
  }

  get isAftercarePlan(): boolean {
    return BillingPlanId.AFTERCARE === this.planId;
  }

  get isProfessionalExecutorPlan(): boolean {
    return [
      BillingPlanId.INDIVIDUAL_PROFESSIONAL_EXECUTOR,
      BillingPlanId.COUPLES_PROFESSIONAL_EXECUTOR
    ].includes(this.planId);
  }

  get isCustomPlan(): boolean {
    return [BillingPlanId.INDIVIDUAL_CUSTOM, BillingPlanId.COUPLES_CUSTOM].includes(this.planId);
  }

  get hasProfessionalExecutor(): boolean {
    return this.isProfessionalExecutorPlan || this.isCustomPlan;
  }

  get isSelfServePlan(): boolean {
    return this.isStandardPlan || this.isBasicPlan || this.isDigitalVaultPlan;
  }

  get isCouplesSelfServePlan(): boolean {
    return this.isCouplesStandardPlan || this.isCouplesBasicPlan;
  }

  get isCompletePlan(): boolean {
    return [BillingPlanId.INDIVIDUAL_COMPLETE_PLAN, BillingPlanId.COUPLES_COMPLETE_PLAN].includes(
      this.planId
    );
  }

  get isEstateRepresentativePlan(): boolean {
    return BillingPlanId.EXECUTOR === this.planId;
  }

  get isCompleteEstatePlan(): boolean {
    return BillingPlanId.COMPLETE_ESTATE === this.planId;
  }

  get isProbatePlan(): boolean {
    return BillingPlanId.PROBATE === this.planId;
  }

  get isProbateAndTaxesPlan(): boolean {
    return BillingPlanId.PROBATE_AND_TAXES === this.planId;
  }

  get isDigitalVaultPlan(): boolean {
    return BillingPlanId.INDIVIDUAL_DIGITAL_VAULT === this.planId;
  }

  get isGuidedPlan(): boolean {
    return [
      BillingPlanId.INDIVIDUAL_COMPLETE_PLAN,
      BillingPlanId.COUPLES_COMPLETE_PLAN,
      BillingPlanId.INDIVIDUAL_PROFESSIONAL_EXECUTOR,
      BillingPlanId.COUPLES_PROFESSIONAL_EXECUTOR,
      BillingPlanId.INDIVIDUAL_CUSTOM,
      BillingPlanId.COUPLES_CUSTOM
    ].includes(this.planId);
  }

  get isCouplesGuidedPlan(): boolean {
    return [
      BillingPlanId.COUPLES_COMPLETE_PLAN,
      BillingPlanId.COUPLES_PROFESSIONAL_EXECUTOR,
      BillingPlanId.COUPLES_CUSTOM
    ].includes(this.planId);
  }

  get isCouplesPlan(): boolean {
    return this.isCouplesBasicPlan || this.isCouplesGuidedPlan;
  }

  static isCouplesPlanId(planId?: BillingPlanId | string | null): boolean {
    return [
      BillingPlanId.COUPLES_BASIC_PLAN,
      BillingPlanId.COUPLES_COMPLETE_PLAN,
      BillingPlanId.COUPLES_PROFESSIONAL_EXECUTOR,
      BillingPlanId.COUPLES_CUSTOM
    ].includes(planId as BillingPlanId);
  }
}
